import { useRef } from "react";
import { motion } from "framer-motion";
import DottedMap from "dotted-map";

export function BelgiumMap({
  dots = [],
  lineColor = "#f97316", // Orange color for Esiteck
}) {
  const svgRef = useRef(null);
  
  // Create a focused map of Belgium region
  const map = new DottedMap({ 
    height: 60, 
    grid: "diagonal",
    avoidWrap: true
  });

  const svgMap = map.getSVG({
    radius: 0.35,
    color: "#1e3a8a40", // Blue with opacity
    shape: "circle",
    backgroundColor: "white",
  });

  // Belgium-specific projection (focused on Belgium coordinates)
  const projectPoint = (lat, lng) => {
    // Belgium bounds: lat 49.5-51.5, lng 2.5-6.4
    const belgiumBounds = {
      minLat: 49.5,
      maxLat: 51.5,
      minLng: 2.5,
      maxLng: 6.4
    };
    
    const x = ((lng - belgiumBounds.minLng) / (belgiumBounds.maxLng - belgiumBounds.minLng)) * 800 + 200;
    const y = ((belgiumBounds.maxLat - lat) / (belgiumBounds.maxLat - belgiumBounds.minLat)) * 300 + 50;
    
    return { x, y };
  };

  const createCurvedPath = (start, end) => {
    const midX = (start.x + end.x) / 2;
    const midY = Math.min(start.y, end.y) - 30;
    return `M ${start.x} ${start.y} Q ${midX} ${midY} ${end.x} ${end.y}`;
  };

  return (
    <div className="w-full aspect-[3/1] bg-white rounded-lg relative font-sans border border-gray-100">
      <img
        src={`data:image/svg+xml;utf8,${encodeURIComponent(svgMap)}`}
        className="h-full w-full [mask-image:linear-gradient(to_bottom,transparent,white_10%,white_90%,transparent)] pointer-events-none select-none opacity-60"
        alt="Belgium map"
        height="400"
        width="1200"
        draggable={false}
      />
      
      <svg
        ref={svgRef}
        viewBox="0 0 1200 400"
        className="w-full h-full absolute inset-0 pointer-events-none select-none"
      >
        {dots.map((dot, i) => {
          const startPoint = projectPoint(dot.start.lat, dot.start.lng);
          const endPoint = projectPoint(dot.end.lat, dot.end.lng);
          return (
            <g key={`path-group-${i}`}>
              <motion.path
                d={createCurvedPath(startPoint, endPoint)}
                fill="none"
                stroke="url(#path-gradient)"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{
                  duration: 1.5,
                  delay: 0.3 * i,
                  ease: "easeOut",
                }}
              />
            </g>
          );
        })}
        
        <defs>
          <linearGradient id="path-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="5%" stopColor={lineColor} stopOpacity="1" />
            <stop offset="95%" stopColor={lineColor} stopOpacity="1" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
        
        {dots.map((dot, i) => (
          <g key={`points-group-${i}`}>
            {/* Start point */}
            <g key={`start-${i}`}>
              <circle
                cx={projectPoint(dot.start.lat, dot.start.lng).x}
                cy={projectPoint(dot.start.lat, dot.start.lng).y}
                r="3"
                fill={lineColor}
              />
              <circle
                cx={projectPoint(dot.start.lat, dot.start.lng).x}
                cy={projectPoint(dot.start.lat, dot.start.lng).y}
                r="3"
                fill={lineColor}
                opacity="0.5"
              >
                <animate
                  attributeName="r"
                  from="3"
                  to="12"
                  dur="2s"
                  begin="0s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  from="0.5"
                  to="0"
                  dur="2s"
                  begin="0s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>
            
            {/* End point */}
            <g key={`end-${i}`}>
              <circle
                cx={projectPoint(dot.end.lat, dot.end.lng).x}
                cy={projectPoint(dot.end.lat, dot.end.lng).y}
                r="3"
                fill={lineColor}
              />
              <circle
                cx={projectPoint(dot.end.lat, dot.end.lng).x}
                cy={projectPoint(dot.end.lat, dot.end.lng).y}
                r="3"
                fill={lineColor}
                opacity="0.5"
              >
                <animate
                  attributeName="r"
                  from="3"
                  to="12"
                  dur="2s"
                  begin="0s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  from="0.5"
                  to="0"
                  dur="2s"
                  begin="0s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>
            
            {/* City labels */}
            {dot.start.label && (
              <text
                x={projectPoint(dot.start.lat, dot.start.lng).x}
                y={projectPoint(dot.start.lat, dot.start.lng).y - 15}
                textAnchor="middle"
                className="fill-blue-900 text-xs font-medium"
              >
                {dot.start.label}
              </text>
            )}
            {dot.end.label && (
              <text
                x={projectPoint(dot.end.lat, dot.end.lng).x}
                y={projectPoint(dot.end.lat, dot.end.lng).y - 15}
                textAnchor="middle"
                className="fill-blue-900 text-xs font-medium"
              >
                {dot.end.label}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}